import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AuthApi, UnauthApi } from '@/api';
import { useStatusBarColor } from '@/hooks/useStatusBarColor';
import { useAuthStore } from '@/stores/AuthStore';
import type { DeviceTokenData } from '@/stores/DeviceStore';
import { STATUS_COLOR, useDeviceInfoStore
// WEBVIEW_TYPE,
} from '@/stores/DeviceStore';
import { isAppVersionAtLeast } from '@/utils';
import { MESSAGE_KEYWORD } from '../_utils/reserved-keywords';
import { useHandleApp } from './useHandleApp';
export const SAFE_AREA_BOTTOM_IOS = 16;

//* 3.1.20 앱 스토어 미배포로 강제 업데이트 임시 비활성 — 배포 후 이 파일의 주석 전부 해제
// 애드팝콘 SSP 광고 SDK 최소 요구 버전 — 미만이면 강제 업데이트
// const MIN_APP_VERSION = '3.1.20';

export const useMountAppData = () => {
  const $ = _c(42);
  const [osType, setAppVersion, tempDeviceTokenData, setTempDeviceTokenData, deviceTokenData, setDeviceTokenData] = useDeviceInfoStore(useShallow(_temp));
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: MESSAGE_KEYWORD.VERSION
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    request: requestAppVersion,
    response: responseAppVersion
  } = useHandleApp(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.NOTIFICATION
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    request: requestNotification,
    response: responseNotification
  } = useHandleApp(t1);
  let t2;
  let t3;
  if ($[2] !== osType || $[3] !== requestAppVersion) {
    t2 = () => {
      if (!osType) {
        return;
      }
      requestAppVersion();
    };
    t3 = [requestAppVersion, osType];
    $[2] = osType;
    $[3] = requestAppVersion;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const isFirstLoading = responseAppVersion?.isSuccess && responseAppVersion.data ? responseAppVersion.data.isFirstLoading : "Y";
  let t4;
  let t5;
  if ($[6] !== osType || $[7] !== responseAppVersion || $[8] !== setAppVersion) {
    t4 = () => {
      if (responseAppVersion?.isSuccess && responseAppVersion?.data) {
        const {
          version
        } = responseAppVersion.data;
        setAppVersion(version);
        if (osType === "ios" && version && isAppVersionAtLeast(version, "3.1.10")) {
          document.documentElement.style.setProperty("--safe-area-bottom", `${SAFE_AREA_BOTTOM_IOS}px`);
          return _temp2;
        }
      }
    };
    t5 = [responseAppVersion, setAppVersion, osType];
    $[6] = osType;
    $[7] = responseAppVersion;
    $[8] = setAppVersion;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[11] !== deviceTokenData || $[12] !== isFirstLoading || $[13] !== osType || $[14] !== requestNotification) {
    t6 = () => {
      if (!osType) {
        return;
      }
      if (!deviceTokenData && isFirstLoading === "N") {
        requestNotification();
      }
    };
    t7 = [deviceTokenData, requestNotification, isFirstLoading, osType];
    $[11] = deviceTokenData;
    $[12] = isFirstLoading;
    $[13] = osType;
    $[14] = requestNotification;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  const isLogin = useAuthStore(_temp3);
  const {
    toastError
  } = useToast();
  let t8;
  if ($[17] !== isLogin) {
    t8 = requestBody => isLogin ? AuthApi["post:/wello/v2/auth/wello-device-info"]({
      body: {
        ...requestBody
      }
    }) : UnauthApi["post:/wello/v2/allows/wello-device-info"]({
      body: {
        ...requestBody
      }
    });
    $[17] = isLogin;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== setDeviceTokenData || $[20] !== tempDeviceTokenData) {
    t9 = () => {
      if (tempDeviceTokenData) {
        setDeviceTokenData(tempDeviceTokenData);
      }
    };
    $[19] = setDeviceTokenData;
    $[20] = tempDeviceTokenData;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== toastError) {
    t10 = error => toastError({
      error
    });
    $[22] = toastError;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    t11 = {
      mutationFn: t8,
      onSuccess: t9,
      onError: t10
    };
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const {
    mutate: saveDeviceTokenMutate
  } = useMutation(t11);
  let t12;
  if ($[28] !== isLogin || $[29] !== responseNotification || $[30] !== setTempDeviceTokenData) {
    t12 = () => {
      if (!responseNotification || isLogin === undefined) {
        return;
      }
      const {
        isSuccess,
        data
      } = responseNotification;
      if (!isSuccess || !data) {
        return;
      }
      setTempDeviceTokenData(data);
    };
    $[28] = isLogin;
    $[29] = responseNotification;
    $[30] = setTempDeviceTokenData;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== isLogin || $[33] !== responseNotification || $[34] !== saveDeviceTokenMutate || $[35] !== setTempDeviceTokenData) {
    t13 = [isLogin, responseNotification, saveDeviceTokenMutate, setTempDeviceTokenData];
    $[32] = isLogin;
    $[33] = responseNotification;
    $[34] = saveDeviceTokenMutate;
    $[35] = setTempDeviceTokenData;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  useEffect(t12, t13);
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      color: STATUS_COLOR.DEFAULT
    };
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  useStatusBarColor(t14);
  let t15;
  let t16;
  if ($[38] !== saveDeviceTokenMutate || $[39] !== tempDeviceTokenData) {
    t15 = () => {
      if (tempDeviceTokenData) {
        saveDeviceTokenMutate(tempDeviceTokenData);
      }
    };
    t16 = [saveDeviceTokenMutate, tempDeviceTokenData];
    $[38] = saveDeviceTokenMutate;
    $[39] = tempDeviceTokenData;
    $[40] = t15;
    $[41] = t16;
  } else {
    t15 = $[40];
    t16 = $[41];
  }
  useEffect(t15, t16);
};
function _temp(state) {
  return [state.osType, state.setAppVersion, state.tempDeviceTokenData, state.setTempDeviceTokenData, state.deviceTokenData, state.setDeviceTokenData];
}
function _temp2() {
  document.documentElement.style.removeProperty("--safe-area-bottom");
}
function _temp3(state_0) {
  return state_0.isLogin;
}